import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GlobalService } from '../../services/global.service';
import { CampagnesService } from '../../services/requests/campagnes.service';
import { UsersService } from '../../services/requests/users.service';
import { DevicesService } from '../../services/requests/devices.service';
import { PortsService } from '../../services/requests/ports.service';

declare function showNotif(message: string, type?: string): void;

interface MachineData {
  deviceId: string | null;
  device: any;
  slots: any[];
  ports: any[];
  stats: {
    envoiTotal: number;
    envoiTotalJour: number;
    envoiTotalHeure: number;
    revenuTotalGeneratedReception: number;
    nbDeTelRecupere: number;
    nbDeTelNonRecupere: number;
    ratioTelRecupere: number;
  };
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {
  devices: any[] = [];
  selectedMachines: (string | null)[] = [null, null];
  selectedDisplayMode: string = 'phoneNumber';
  machinesData: MachineData[] = [];
  campagnesEnPlay: any[] = [];
  isLoading = false;
  isAdmin = false;
  isSousAdmin = false;
  private refreshInterval: any = null;
  selectedView: string = 'machines'; // 'stats', 'machines', 'campagnes'
  selectedDeviceForStats: string | null = null;
  
  // Données pour les stats
  statsData: any = {
    phoneNumbersByDay: [],
    smsByHour: [],
    machinesDeadChips: [],
    deviceStats: null
  };

  constructor(
    private httpClient: HttpClient,
    private globalService: GlobalService,
    private campagnesService: CampagnesService,
    private usersService: UsersService,
    private devicesService: DevicesService,
    private portsService: PortsService,
  ) {}

  ngOnInit(): void {
    this.isAdmin = localStorage.getItem('isAdmin') === 'true';
    this.isSousAdmin = localStorage.getItem('isSousAdmin') === 'true';
    
    // Récupérer la vue depuis localStorage
    const savedView = localStorage.getItem('dashboardView');
    if (savedView && ['stats', 'machines'].includes(savedView)) {
      this.selectedView = savedView;
    }
    
    // Récupérer les machines sélectionnées depuis localStorage
    const savedMachine1 = localStorage.getItem('dashboardMachine1');
    const savedMachine2 = localStorage.getItem('dashboardMachine2');
    if (savedMachine1) {
      this.selectedMachines[0] = savedMachine1;
    }
    if (savedMachine2) {
      this.selectedMachines[1] = savedMachine2;
    }
    
    // Initialiser les données des machines
    this.machinesData = [
      { deviceId: null, device: null, slots: [], ports: [], stats: this.getEmptyStats() },
      { deviceId: null, device: null, slots: [], ports: [], stats: this.getEmptyStats() }
    ];
    
    this.loadDevices();
    this.loadCampagnesEnPlay();
    
    // Charger les stats si on est en vue stats
    if (this.selectedView === 'stats') {
      this.loadStatsData();
    }
    
    // Démarrer le rafraîchissement automatique toutes les 5 secondes
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    // Nettoyer l'interval lors de la destruction du composant
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  startAutoRefresh(): void {
    // Rafraîchir toutes les 5 secondes (5000ms)
    this.refreshInterval = setInterval(() => {
      this.refreshAll();
    }, 5000);
  }

  refreshAll(): void {
    // Rafraîchir les campagnes en play (toujours, car utilisées dans le header aussi)
    this.loadCampagnesEnPlay();
    
    // Rafraîchir les données de toutes les machines sélectionnées
    this.selectedMachines.forEach((deviceId, index) => {
      if (deviceId) {
        this.loadMachineData(deviceId, index);
      }
    });
    
    // Rafraîchir les stats si on est en vue stats et qu'un device est sélectionné
    if (this.selectedView === 'stats' && this.selectedDeviceForStats) {
      this.loadStatsData();
    }
    
    // Rafraîchir les devices pour mettre à jour le header
    this.loadDevices();
  }

  onViewChange(): void {
    // Sauvegarder la vue dans localStorage
    localStorage.setItem('dashboardView', this.selectedView);
    
    if (this.selectedView === 'stats') {
      // Récupérer le device sélectionné depuis localStorage
      const savedDevice = localStorage.getItem('dashboardStatsDevice');
      if (savedDevice) {
        this.selectedDeviceForStats = savedDevice;
      }
      this.loadStatsData();
    }
  }

  onDeviceForStatsChange(): void {
    // Sauvegarder le device sélectionné dans localStorage
    if (this.selectedDeviceForStats) {
      localStorage.setItem('dashboardStatsDevice', this.selectedDeviceForStats);
    } else {
      localStorage.removeItem('dashboardStatsDevice');
    }
    this.loadStatsData();
  }

  loadStatsData(): void {
    if (!this.selectedDeviceForStats) {
      // Pas de device sélectionné, réinitialiser les stats
      this.statsData.phoneNumbersByDay = [];
      this.statsData.smsByHour = [];
      this.statsData.deviceStats = null;
      return;
    }

    // Charger les données du device sélectionné
    this.devicesService.getById(this.selectedDeviceForStats).subscribe({
      next: (deviceRes: any) => {
        if (deviceRes.status && deviceRes.data) {
          const device = deviceRes.data;
          
          // Charger les slots du device
          this.portsService.getSlotsByDevice(this.selectedDeviceForStats!).subscribe({
            next: (slotsRes: any) => {
              const slots = slotsRes.status && slotsRes.data ? slotsRes.data : [];
              this.calculateDeviceStats(device, slots);
            },
            error: () => {
              console.error("Erreur lors de la récupération des slots pour les stats");
            }
          });
        }
      },
      error: () => {
        console.error("Erreur lors de la récupération du device pour les stats");
      }
    });
  }

  calculateDeviceStats(device: any, slots: any[]): void {
    // Calculer les stats pour le device sélectionné
    const totalChips = (device.nbPorts || 0) * (device.nbSlots || 0);
    const deadChips = slots.filter((s: any) => (s.totalEnvoi || 0) === 0).length;
    const deadRatio = totalChips > 0 ? Math.round((deadChips / totalChips) * 100) : 0;
    
    // Calculer les numéros de téléphone récupérés par jour (7 derniers jours)
    const phoneNumbersByDay = this.calculatePhoneNumbersByDay(slots);
    
    // Calculer les SMS envoyés par heure (24 heures)
    const smsByHour = this.calculateSmsByHour(slots);
    
    // Calculer les totaux
    const totalPhoneNumbers = slots.filter((s: any) => 
      s.phoneNumber && 
      s.phoneNumber.toString().trim() !== '' && 
      s.phoneNumber.toString().trim() !== '-1' &&
      s.phoneNumber.toString().trim() !== '0'
    ).length;
    
    const totalSms = slots.reduce((sum, s: any) => sum + (s.totalEnvoi || 0), 0);
    const totalSmsJour = slots.reduce((sum, s: any) => sum + (s.totalEnvoiJour || 0), 0);
    const totalSmsHeure = slots.reduce((sum, s: any) => sum + (s.totalEnvoiHeure || 0), 0);
    
    this.statsData.deviceStats = {
      deviceName: device.name,
      totalChips: totalChips,
      deadChips: deadChips,
      deadRatio: deadRatio,
      totalPhoneNumbers: totalPhoneNumbers,
      totalSms: totalSms,
      totalSmsJour: totalSmsJour,
      totalSmsHeure: totalSmsHeure
    };
    
    this.statsData.phoneNumbersByDay = phoneNumbersByDay;
    this.statsData.smsByHour = smsByHour;
  }

  calculatePhoneNumbersByDay(slots: any[]): any[] {
    // Calculer les numéros récupérés par jour
    // Pour l'instant, on utilise une répartition basée sur le total
    // TODO: Si l'API fournit des données par jour, les utiliser
    const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    const totalPhones = slots.filter((s: any) => 
      s.phoneNumber && 
      s.phoneNumber.toString().trim() !== '' && 
      s.phoneNumber.toString().trim() !== '-1' &&
      s.phoneNumber.toString().trim() !== '0'
    ).length;
    
    // Répartir de manière réaliste (plus de numéros en début de semaine)
    const distribution = [1.2, 1.1, 1.0, 0.9, 0.8, 0.7, 0.6]; // Facteurs de distribution
    const totalFactor = distribution.reduce((a, b) => a + b, 0);
    const avgPerDay = totalPhones / totalFactor;
    
    const data = days.map((day, index) => {
      const value = Math.round(avgPerDay * distribution[index]);
      return {
        day: day,
        value: value,
        percentage: 0
      };
    });
    
    const maxValue = Math.max(...data.map(d => d.value), 1);
    data.forEach(d => {
      d.percentage = maxValue > 0 ? (d.value / maxValue) * 100 : 0;
    });
    
    return data;
  }

  calculateSmsByHour(slots: any[]): any[] {
    // Calculer les SMS par heure basés sur totalEnvoiHeure
    // Répartir le totalEnvoiHeure sur les 24 heures de manière réaliste
    const totalSmsHeure = slots.reduce((sum, s: any) => sum + (s.totalEnvoiHeure || 0), 0);
    
    // Distribution réaliste (plus d'activité en journée)
    const hourDistribution = [
      0.3, 0.2, 0.1, 0.1, 0.2, 0.4, 0.6, 0.8, 1.0, 1.2, 1.3, 1.4, // 0h-11h
      1.5, 1.4, 1.3, 1.2, 1.1, 1.0, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4  // 12h-23h
    ];
    const totalFactor = hourDistribution.reduce((a, b) => a + b, 0);
    const avgPerHour = totalSmsHeure / totalFactor;
    
    const data = Array.from({ length: 24 }, (_, i) => {
      const value = Math.round(avgPerHour * hourDistribution[i]);
      return {
        hour: i,
        value: value,
        percentage: 0
      };
    });
    
    const maxValue = Math.max(...data.map(d => d.value), 1);
    data.forEach(d => {
      d.percentage = maxValue > 0 ? (d.value / maxValue) * 100 : 0;
    });
    
    return data;
  }

  getBarColor(index: number): string {
    const colors = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe', '#43e97b', '#fa709a'];
    return colors[index % colors.length];
  }

  getEmptyStats() {
    return {
      envoiTotal: 0,
      envoiTotalJour: 0,
      envoiTotalHeure: 0,
      revenuTotalGeneratedReception: 0,
      nbDeTelRecupere: 0,
      nbDeTelNonRecupere: 0,
      ratioTelRecupere: 0
    };
  }

  loadDevices(): void {
    // Ne pas activer isLoading pour les rafraîchissements automatiques
    const isInitialLoad = !this.devices.length;
    if (isInitialLoad) {
      this.isLoading = true;
    }
    
    this.devicesService.getAll().subscribe({
      next: (res: any) => {
        if (isInitialLoad) {
          this.isLoading = false;
        }
        if (res.status && res.data?.devices) {
          this.devices = res.data.devices.filter((d: any) => !d.isArchive);
          
          // Après le chargement des devices, charger les machines sélectionnées
          if (isInitialLoad) {
            this.selectedMachines.forEach((deviceId, index) => {
              if (deviceId) {
                // Vérifier que le device existe encore dans la liste
                const deviceExists = this.devices.some(d => d._id === deviceId);
                if (deviceExists) {
                  this.loadMachineData(deviceId, index);
                } else {
                  // Le device n'existe plus, réinitialiser
                  this.selectedMachines[index] = null;
                  localStorage.removeItem(`dashboardMachine${index + 1}`);
                }
              }
            });
          }
        }
      },
      error: () => {
        if (isInitialLoad) {
          this.isLoading = false;
        }
        console.error("Erreur lors de la récupération des machines");
      }
    });
  }

  loadCampagnesEnPlay(): void {
    // Charger tous les clients d'abord
    this.usersService.getAll().subscribe({
      next: (clientsRes: any) => {
        const clients = clientsRes.status ? (clientsRes.data?.users || []) : [];
        
        // Charger toutes les campagnes
        this.campagnesService.getAll().subscribe({
          next: async (res: any) => {
            if (res.status && res.data) {
              const allCampagnes = res.data.campaigns || res.data || [];
              
              // Filtrer d'abord les campagnes en cours
              const campagnesEnCours = allCampagnes.filter((c: any) => 
                (c.isOn === '1' || c.isOn === 'on') && c.isArchived !== '1'
              );
              
              // Enrichir les campagnes avec les stats et les infos client (SP-GetSmsApi)
              const enrichedCampagnes = await Promise.all(
                campagnesEnCours.map(async (c: any) => {
                  const client = clients.find((cli: any) => cli._id === c.idClient);
                  
                  // Récupérer les stats de la campagne via SP-GetSmsApi
                  const stats = await this.fetchStats('campagne', c._id);
                  
                  return {
                    ...c,
                    nomClient: client?.identifiant || '',
                    typeClient: client?.typeAccount || '',
                    total: stats.total || 0,
                    failed: stats.failed || 0,
                    reallySend: stats.reallySend || 0,
                    envoyer: stats.envoyer || 0,
                    enAttente: stats.enAttente || 0,
                    nombreStop: stats.stop || 0,
                    nombreTooLong: stats.tooLong || 0,
                    nombreDuplicate: stats.duplicate || 0,
                    nombreReception: stats.reception || 0
                  };
                })
              );
              
              this.campagnesEnPlay = enrichedCampagnes;
            }
          },
          error: () => {
            console.error("Erreur récupération campagnes");
          }
        });
      },
      error: () => {
        console.error("Erreur récupération clients");
        // Essayer quand même de charger les campagnes sans les clients
        this.campagnesService.getAll().subscribe({
          next: async (res: any) => {
            if (res.status && res.data) {
              const allCampagnes = res.data.campaigns || res.data || [];
              
              const campagnesEnCours = allCampagnes.filter((c: any) => 
                (c.isOn === '1' || c.isOn === 'on') && c.isArchived !== '1'
              );
              
              const enrichedCampagnes = await Promise.all(
                campagnesEnCours.map(async (c: any) => {
                  const stats = await this.fetchStats('campagne', c._id);
                  return {
                    ...c,
                    nomClient: '',
                    typeClient: '',
                    total: stats.total || 0,
                    failed: stats.failed || 0,
                    reallySend: stats.reallySend || 0,
                    envoyer: stats.envoyer || 0,
                    enAttente: stats.enAttente || 0,
                    nombreStop: stats.stop || 0,
                    nombreTooLong: stats.tooLong || 0,
                    nombreDuplicate: stats.duplicate || 0,
                    nombreReception: stats.reception || 0
                  };
                })
              );
              
              this.campagnesEnPlay = enrichedCampagnes;
            }
          },
          error: () => {
            console.error("Erreur récupération campagnes");
          }
        });
      }
    });
  }

  private fetchStats(type: 'campagne' | 'user', id: string): Promise<any> {
    return new Promise(resolve => {
      this.campagnesService.getStatsSms({ id, type }).subscribe({
        next: (response: any) => {
          if (!response.status) {
            console.warn(`⚠️ SP-GetSmsApi (${type}) a répondu sans succès :`, response.message);
            return resolve({});
          }
          resolve(response.data || {});
        },
        error: (error: any) => {
          console.error(`❌ Erreur SP-GetSmsApi (${type}) :`, error);
          resolve({});
        }
      });
    });
  }

  onDisplayModeChange(): void {
    // Recharger les données pour toutes les machines sélectionnées
    this.selectedMachines.forEach((deviceId, index) => {
      if (deviceId) {
        this.loadMachineData(deviceId, index);
      }
    });
  }

  onMachineChange(index: number, deviceId: string | null): void {
    // Mettre à jour selectedMachines d'abord
    this.selectedMachines[index] = deviceId;
    
    // Sauvegarder dans localStorage
    if (deviceId) {
      localStorage.setItem(`dashboardMachine${index + 1}`, deviceId);
    } else {
      localStorage.removeItem(`dashboardMachine${index + 1}`);
    }
    
    if (deviceId) {
      this.loadMachineData(deviceId, index);
    } else {
      // Réinitialiser les données de cette machine
      this.machinesData[index] = {
        deviceId: null,
        device: null,
        slots: [],
        ports: [],
        stats: this.getEmptyStats()
      };
    }
  }

  loadMachineData(deviceId: string, index: number): void {
    // Récupérer les infos du device
    this.devicesService.getById(deviceId).subscribe({
      next: (deviceRes: any) => {
        if (deviceRes.status && deviceRes.data) {
          const device = deviceRes.data;
          
          // Récupérer les ports et les slots en parallèle
          let portsData: any = null;
          let slotsData: any = null;
          
          this.portsService.get(deviceId).subscribe({
            next: (portsRes: any) => {
              portsData = portsRes;
              if (slotsData !== null) {
                this.processMachineData(deviceId, index, device, portsData, slotsData);
              }
            },
            error: (error) => {
              console.error(`Erreur récupération ports pour machine ${index}:`, error);
              portsData = { status: false, data: [] };
              if (slotsData !== null) {
                this.processMachineData(deviceId, index, device, portsData, slotsData);
              }
            }
          });
          
          this.portsService.getSlotsByDevice(deviceId).subscribe({
            next: (slotsRes: any) => {
              slotsData = slotsRes;
              if (portsData !== null) {
                this.processMachineData(deviceId, index, device, portsData, slotsData);
              }
            },
            error: (error) => {
              console.error(`Erreur récupération slots pour machine ${index}:`, error);
              slotsData = { status: false, data: [] };
              if (portsData !== null) {
                this.processMachineData(deviceId, index, device, portsData, slotsData);
              }
            }
          });
        }
      },
      error: (error) => {
        console.error(`Erreur récupération device ${index}:`, error);
      }
    });
  }

  private processMachineData(deviceId: string, index: number, device: any, portsRes: any, slotsRes: any): void {
    const ports = portsRes?.status && portsRes?.data ? portsRes.data : [];
    const slots = slotsRes?.status && slotsRes?.data ? slotsRes.data : [];
    
    // Trier les ports par numéro
    ports.sort((a: any, b: any) => a.number - b.number);
    
    // Calculer les statistiques
    const stats = this.calculateStats(device, slots);
    
    // Mettre à jour les données de la machine
    // IMPORTANT: Ne pas écraser deviceId dans selectedMachines
    // S'assurer que selectedMachines[index] reste cohérent avec deviceId
    this.machinesData[index] = {
      deviceId: deviceId,
      device: device,
      slots: slots,
      ports: ports,
      stats: stats
    };
    
    // S'assurer que selectedMachines[index] est bien défini pour maintenir la sélection
    if (this.selectedMachines[index] !== deviceId) {
      this.selectedMachines[index] = deviceId;
    }
  }

  calculateStats(device: any, slots: any[]): MachineData['stats'] {
    const nbPorts = device.nbPorts || 0;
    const nbSlots = device.nbSlots || 0;
    const totalPuces = nbPorts * nbSlots;

    let envoiTotal = 0;
    let envoiTotalJour = 0;
    let envoiTotalHeure = 0;
    let revenuTotalGeneratedReception = 0;
    let nbDeTelRecupere = 0;

    slots.forEach((slot: any) => {
      envoiTotal += slot.totalEnvoi || 0;
      envoiTotalJour += slot.totalEnvoiJour || 0;
      envoiTotalHeure += slot.totalEnvoiHeure || 0;
      revenuTotalGeneratedReception += slot.revenuGeneratedReception || 0;
      
      const phoneNumber = slot.phoneNumber;
      if (phoneNumber && 
          phoneNumber.toString().trim() !== '' && 
          phoneNumber.toString().trim() !== '-1' &&
          phoneNumber.toString().trim() !== '0') {
        nbDeTelRecupere++;
      }
    });

    const nbDeTelNonRecupere = totalPuces - nbDeTelRecupere;
    const ratioTelRecupere = totalPuces > 0 ? Math.round((nbDeTelRecupere / totalPuces) * 100 * 100) / 100 : 0;

    return {
      envoiTotal,
      envoiTotalJour,
      envoiTotalHeure,
      revenuTotalGeneratedReception,
      nbDeTelRecupere,
      nbDeTelNonRecupere,
      ratioTelRecupere
    };
  }

  hasSelectedMachines(): boolean {
    return this.selectedMachines.some(id => id !== null);
  }

  getMachineName(deviceId: string | null): string {
    if (!deviceId) return '';
    const device = this.devices.find(d => d._id === deviceId);
    return device?.name || '';
  }

  getPortNumbers(device: any): number[] {
    if (!device || !device.nbPorts) return [];
    return Array.from({ length: device.nbPorts }, (_, i) => i + 1);
  }

  getSlotNumbers(device: any): string[] {
    if (!device || !device.nbSlots) return [];
    return Array.from({ length: device.nbSlots }, (_, i) => 
      String(i + 1).padStart(2, '0')
    );
  }

  getCellValue(machineData: MachineData, port: number, slot: string): string {
    if (!machineData || !machineData.slots) {
      if (this.selectedDisplayMode === 'phoneNumber') return '';
      return '0';
    }
    
    const slotKey = `${port}.${slot}`;
    const slotData = machineData.slots.find((s: any) => s.port === slotKey);
    
    if (!slotData) {
      if (this.selectedDisplayMode === 'phoneNumber') return '';
      return '0';
    }

    switch (this.selectedDisplayMode) {
      case 'phoneNumber':
        // Retourner le phoneNumber pour pouvoir vérifier s'il existe
        return slotData.phoneNumber || '';
      case 'totalEnvoi':
        return (slotData.totalEnvoi || 0).toString();
      case 'totalEnvoiHeure':
        return (slotData.totalEnvoiHeure || 0).toString();
      case 'totalEnvoiJour':
        return (slotData.totalEnvoiJour || 0).toString();
      case 'revenuGeneratedReception':
        const revenu = slotData.revenuGeneratedReception || 0;
        return revenu > 0 ? revenu.toFixed(2) : '0';
      case 'portStatus':
        // Pour le statut, on retourne une chaîne vide car on utilise les classes CSS
        return '';
      default:
        return '0';
    }
  }
  
  shouldShowIndicator(): boolean {
    return this.selectedDisplayMode === 'phoneNumber' || this.selectedDisplayMode === 'portStatus';
  }
  
  hasValidPhoneNumber(machineData: MachineData, port: number, slot: string): boolean {
    if (!machineData || !machineData.slots) return false;
    const slotKey = `${port}.${slot}`;
    const slotData = machineData.slots.find((s: any) => s.port === slotKey);
    if (!slotData) return false;
    const phoneNumber = slotData.phoneNumber;
    return phoneNumber && 
           phoneNumber.toString().trim() !== '' && 
           phoneNumber.toString().trim() !== '-1' &&
           phoneNumber.toString().trim() !== '0';
  }

  getCellClass(machineData: MachineData, port: number, slot: string): string {
    const baseClass = 'table-cell';
    
    if (this.selectedDisplayMode === 'phoneNumber') {
      // Utiliser la même logique que dans ports
      const hasPhoneNumber = this.hasValidPhoneNumber(machineData, port, slot);
      return hasPhoneNumber ? `${baseClass} cell-phone-number` : baseClass;
    }

    if (this.selectedDisplayMode === 'portStatus') {
      // Utiliser la même logique que dans le composant ports
      return this.getPortSlotClass(machineData, port, slot);
    }

    // Pour les autres modes, on peut utiliser des classes basées sur la valeur
    const value = this.getCellValue(machineData, port, slot);
    const numValue = parseFloat(value) || 0;
    
    if (numValue === 0) {
      return `${baseClass} cell-zero`;
    }
    
    return `${baseClass} cell-has-value`;
  }

  getPortSlotClass(machineData: MachineData, port: number, slot: string): string {
    const baseClass = 'table-cell';
    const slotKey = `${port}.${slot}`;
    const slotData = machineData.slots?.find((s: any) => s.port === slotKey);
    
    // Vérifier si le slot a envoyé au moins 1 SMS/MMS
    const totalEnvoi = slotData?.totalEnvoi || 0;
    
    if (totalEnvoi >= 1) {
      return `${baseClass} cell-port-open`;
    }
    
    // Trouver le port correspondant
    const portData = machineData.ports?.find((p: any) => p.number === port);
    
    if (!portData || !portData.slotActuel) {
      return `${baseClass} cell-port-closed`;
    }

    // slotActuel est un objet Slot avec une propriété 'port' au format "1.01", "2.03", etc.
    const slotActuelPort = portData.slotActuel.port || '';
    
    // Extraire le numéro de slot du slotActuel (ex: "1.01" -> "01")
    const slotActuelNumber = slotActuelPort.includes('.') ? slotActuelPort.split('.')[1] : '';
    
    // Comparer avec le slot actuel (slot est déjà au format "01", "02", etc.)
    const isCurrentSlot = slotActuelNumber === slot;
    
    // RÈGLE : Seul le slot actuel peut être ouvert/fermé
    // Tous les autres slots sont TOUJOURS fermés
    let state = 'closed'; // Par défaut fermé
    
    // Seulement si c'est le slot actuel, vérifier l'état isOpen
    if (isCurrentSlot) {
      if (portData.isOpen === 'o') {
        state = 'open'; // Vert si ouvert
      } else {
        state = 'closed'; // Rouge si fermé
      }
    }
    // Tous les autres slots sont TOUJOURS fermés
    else {
      state = 'closed'; // Rouge
    }

    return `${baseClass} cell-port-${state}`;
  }

  getCellTitle(machineData: MachineData, port: number, slot: string): string {
    const slotKey = `${port}.${slot}`;
    const slotData = machineData.slots.find((s: any) => s.port === slotKey);
    
    let title = `Port ${port} - Slot ${slot}`;
    
    if (slotData) {
      if (this.selectedDisplayMode === 'phoneNumber') {
        const phoneNumber = slotData.phoneNumber;
        if (phoneNumber && phoneNumber.toString().trim() !== '' && phoneNumber.toString().trim() !== '-1' && phoneNumber.toString().trim() !== '0') {
          title += ` - Cliquez pour copier : ${phoneNumber}`;
        }
      }
      if (this.selectedDisplayMode === 'totalEnvoi') {
        title += `\nTotal: ${slotData.totalEnvoi || 0}`;
      }
      if (this.selectedDisplayMode === 'totalEnvoiHeure') {
        title += `\nHeure: ${slotData.totalEnvoiHeure || 0}`;
      }
      if (this.selectedDisplayMode === 'totalEnvoiJour') {
        title += `\nJour: ${slotData.totalEnvoiJour || 0}`;
      }
      if (this.selectedDisplayMode === 'revenuGeneratedReception') {
        title += `\nRevenu: ${slotData.revenuGeneratedReception || 0}`;
      }
    }
    
    return title;
  }

  onCellClick(machineData: MachineData, port: number, slot: string): void {
    if (this.selectedDisplayMode === 'phoneNumber') {
      const phoneNumber = this.getCellValue(machineData, port, slot);
      if (phoneNumber && phoneNumber.toString().trim() !== '' && phoneNumber.toString().trim() !== '-1' && phoneNumber.toString().trim() !== '0') {
        this.copyPhoneNumberToClipboard(phoneNumber.toString());
      }
    }
  }

  copyPhoneNumberToClipboard(phoneNumber: string): void {
    const texte = phoneNumber?.trim();
    if (!texte || texte === '' || texte === '-1') {
      return;
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(texte).then(() => {
        // Utiliser showNotif si disponible, sinon console.log
        if (typeof showNotif !== 'undefined') {
          showNotif('Numero de tel copié dans le presse papier', 'success');
        } else {
          console.log('Numéro copié:', texte);
        }
      }).catch(() => {
        if (typeof showNotif !== 'undefined') {
          showNotif('❌ Erreur lors de la copie du numéro', 'error');
        } else {
          console.error('Erreur lors de la copie');
        }
      });
    }
  }

  formatCampagneDate(c: any): string {
    const d = c.isScheduled === '1' ? c.schedule : c.date;
    const date = new Date(d);
    return date.toLocaleString('fr-FR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getActiveMachinesCount(): number {
    return this.devices.filter(d => d.isUsed === true).length;
  }

  compareDeviceIds(deviceId1: string | null, deviceId2: string | null): boolean {
    // Comparer les IDs de manière stricte
    return deviceId1 === deviceId2;
  }
}

